<template>
  <div class="settings-vital-signs-container m-4">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Vital Signs</h5>
      <b-button
        class="pt-2 pb-2"
        size="sm"
        @click="addVitalSign"
        variant="primary"
        >New</b-button
      >
    </div>
    <grid
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="items"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
    ></grid>
    <b-modal
      centered
      ref="form-modal"
      :title="formId ? 'Edit Vital Sign' : 'Add Vital Sign'"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok="submitVitalSign"
      no-close-on-backdrop
      header-class="bg-purple"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <template v-if="formId" v-slot:modal-header>
        <div class="w-100 d-flex flex-row justify-content-between">
          <h5 class="modal-title">Edit VitalSign</h5>
          <div class="d-flex justify-content-end">
            <div class="d-flex align-items-center">
              <button
                type="button"
                aria-label="Close"
                class="close text-white"
                @click="$refs['form-modal'].hide"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      </template>
      <b-form-group
        label="Name"
        :state="formErrors.name.length == 0 ? null : false"
        :invalid-feedback="formErrors.name.join(', ')"
      >
        <b-form-input
          v-model="form.name"
          maxLength="40"
          :disabled="!form.editable"
          :state="formErrors.name.length == 0 ? null : false"
        ></b-form-input>
      </b-form-group>
      <b-form-group label="Unit">
        <b-form-input
          v-model="form.unit"
          maxLength="10"
          :disabled="!form.editable"
        ></b-form-input>
      </b-form-group>
      <b-form-group
        label="Type"
        :state="formErrors.type.length == 0 ? null : false"
        :invalid-feedback="formErrors.type.join(', ')"
      >
        <b-form-select
          v-model="form.type"
          :options="types"
          :disabled="!form.editable"
          :state="formErrors.type.length == 0 ? null : false"
        ></b-form-select>
      </b-form-group>
      <b-form-group label="Values" v-if="form.type == 'singleSelection'">
        <tags-input v-model="form.data" :listDraggable="true" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import vitalSignServer from "../../server/vitalSign";
import { mapState, mapActions } from "vuex";
import { validateRequired } from "../../helpers/validation";
import TagsInput from "../controls/TagsInput.vue";

const types = [
  {
    code: "number",
    title: "Number"
  },
  {
    code: "text",
    title: "Text"
  },
  {
    code: "singleSelection",
    title: "Single Selection"
  },
  {
    code: "numberPair",
    title: "Number Pair"
  }
];

export default {
  name: "VitalSignCrud",
  components: {
    Grid,
    TagsInput
  },
  data() {
    return {
      list: [],
      formId: null,
      form: {
        name: "",
        unit: "",
        type: null,
        data: []
      },
      formErrors: {
        name: [],
        type: []
      },
      optionValue: ""
    };
  },
  mounted() {
    this.getVitalSigns();
    this.fetchRoles();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("layout", ["fetchRoles"]),
    ...mapActions("client", ["fetchVitalSigns"]),
    async getVitalSigns() {
      this.list = await vitalSignServer.query();
    },
    validateVitalSignSubmit() {
      let withoutErrors = true;

      this.avatarErrors = [];

      this.formErrors.name = [];
      if (!validateRequired(this.form.name)) {
        this.formErrors.name.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.type = [];
      if (!validateRequired(this.form.type)) {
        this.formErrors.type.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitVitalSign(modalEvent) {
      modalEvent.preventDefault();
      const result = this.validateVitalSignSubmit();
      if (result) {
        const formData = {
          ...this.form,
          data: this.form.data ? JSON.stringify(this.form.data) : ""
        };
        if (this.formId) {
          await vitalSignServer.update(this.formId, formData);
        } else {
          await vitalSignServer.create(formData);
        }
        this.getVitalSigns();
        this.fetchVitalSigns();
        this.showAlertSuccess();
        this.$refs["form-modal"].hide();
      }
    },
    addVitalSign() {
      this.formId = null;
      this.form = {
        name: "",
        unit: "",
        type: null,
        data: [],
        editable: true
      };
      this.optionValue = "";
      this.$refs["form-modal"].show();
    }
  },
  computed: {
    ...mapState("layout", {
      roles: state =>
        state.roles.map(role => ({
          value: role.id,
          text: role.name
        }))
    }),
    items() {
      return this.list.map(item => ({
        ...item
      }));
    },
    types() {
      return types.map(type => ({
        value: type.code,
        text: type.title
      }));
    },
    columns() {
      let columns = [
        {
          text: "Name",
          field: "name",
          flex: 1,
          editor: false
        },
        {
          text: "Unit",
          field: "unit",
          flex: 1,
          editor: false
        },
        {
          text: "",
          type: "action",
          cellCls: "pointer-cell",
          field: "action",
          align: "right",
          width: 110,
          actions: [
            {
              tooltip: "Edit",
              renderer: ({ record }) =>
                record.editable
                  ? `<img class="b-action-item" src="${
                      this.isStandalone()
                        ? `/icons/edit.svg`
                        : `/desktop/icons/edit.svg`
                    }">`
                  : "",
              onClick: async ({ record }) => {
                this.formId = record.id;
                const form = await vitalSignServer.get(this.formId);
                this.form = {
                  ...form,
                  data: form.data ? JSON.parse(form.data) : []
                };
                this.$refs["form-modal"].show();
              }
            },
            {
              tooltip: "Delete",
              renderer: ({ record }) =>
                record.editable
                  ? `<img class="b-action-item ml-2" src="${
                      this.isStandalone()
                        ? `/icons/delete.svg`
                        : `/desktop/icons/delete.svg`
                    }">`
                  : "",
              onClick: ({ record }) => {
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(async () => {
                    await vitalSignServer.delete(record.id);
                    this.getVitalSigns();
                    this.fetchVitalSigns();
                    this.showAlertSuccess();
                  })
                  .catch(function() {});
              }
            }
          ]
        }
      ];
      return columns;
    }
  }
};
</script>

<style>
.settings-vital-signs-container .b-grid-container .b-container {
  max-height: calc(100vh - 281px);
}
</style>
